(() => {
  if (customElements.get('ac-about-scene')) return;
  // The reference uses cubic-bezier(.75,0,.25,1). Solve x(t) once per
  // animation sample; this pure easing function needs no extra plugin or DOM reads.
  const possibleEase = progress => {
    let t = progress;
    for (let i = 0; i < 6; i++) t -= ((2.5 * t - 3.75) * t * t + 2.25 * t - progress) / ((7.5 * t - 7.5) * t + 2.25);
    return t * t * (3 - 2 * t);
  };
  class ACAboutScene extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.reduced.addEventListener('change', () => this.setupMotion(), { signal });
      this.motionObserver = new MutationObserver(() => this.setupMotion());
      this.motionObserver.observe(document.body, { attributes: true, attributeFilter: ['data-ac-motion'] });
      this.finePointer = matchMedia('(hover: hover) and (pointer: fine)');
      if (this.dataset.scene === 'possible') this.setupPossibleInteractions(signal);
      if (this.dataset.scene === 'finale') this.setupFinaleInteractions(signal);
      if (document.readyState === 'loading' || (!window.ScrollTrigger && document.readyState !== 'complete')) document.addEventListener('DOMContentLoaded', () => this.setupMotion(), { once: true, signal });
      else this.setupMotion();
    }

    setupPossibleInteractions(signal) {
      this.finePointer.addEventListener('change', () => {
        if (!this.finePointer.matches) this.hoveredLetter = null;
        this.updateCycle();
      }, { signal });
      this.cycleIndex = 0;
      this.addEventListener('click', event => {
        const letter = event.target.closest('.ac-about-possible__letter');
        if (letter) {
          if (!letter.matches(':focus-visible')) this.focusedLetter = null;
          const pressed = letter.getAttribute('aria-pressed') !== 'true';
          this.querySelectorAll('.ac-about-possible__letter').forEach(button => button.setAttribute('aria-pressed', String(button === letter && pressed)));
          this.syncVideo(letter, pressed || letter === this.hoveredLetter || letter === this.focusedLetter);
          this.updateCycle();
        }
      }, { signal });
      ['pointerover', 'focusin'].forEach(type => this.addEventListener(type, event => {
        const letter = event.target.closest('.ac-about-possible__letter');
        if (letter && (type === 'focusin' || this.finePointer.matches)) {
          if (type === 'focusin') this.focusedLetter = letter;
          else this.hoveredLetter = letter;
          this.syncVideo(letter, true);
        }
        this.updateCycle();
      }, { signal }));
      ['pointerout', 'focusout'].forEach(type => this.addEventListener(type, event => {
        const letter = event.target.closest('.ac-about-possible__letter');
        if (!letter || letter.contains(event.relatedTarget)) {
          if (type === 'focusout') this.updateCycle();
          return;
        }
        if (type === 'focusout' && this.focusedLetter === letter) this.focusedLetter = null;
        if (type === 'pointerout' && this.hoveredLetter === letter) this.hoveredLetter = null;
        if (letter.getAttribute('aria-pressed') !== 'true' && letter !== this.hoveredLetter && letter !== this.focusedLetter) letter.querySelector('video')?.pause();
        this.updateCycle();
      }, { signal }));
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.querySelectorAll('video').forEach(video => video.pause());
        }
        this.updateCycle();
      }, { signal });
      this.visibility = new IntersectionObserver(entries => {
        this.lettersVisible = entries[0].isIntersecting;
        if (!entries[0].isIntersecting) this.querySelectorAll('video').forEach(video => video.pause());
        this.updateCycle();
      });
      this.visibility.observe(this.querySelector('.ac-about-possible__letters') || this);
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          this.querySelectorAll('.ac-about-possible__letter').forEach(button => button.setAttribute('aria-pressed', 'false'));
          this.querySelectorAll('video').forEach(video => video.pause());
          this.updateCycle();
        }
      }, { signal });
    }

    setupFinaleInteractions(signal) {
      const burstButton = this.querySelector('[data-about-burst]');
      if (!burstButton) return;
      this.finalePointerNear = true;
      if ('IntersectionObserver' in window) {
        this.finalePointerObserver = new IntersectionObserver(entries => {
          this.finalePointerNear = entries[0].isIntersecting;
          this.syncFinaleCursor();
        }, { rootMargin: '100% 0px' });
        this.finalePointerObserver.observe(this);
      }
      window.addEventListener('pointermove', event => {
        if (event.pointerType === 'touch') return;
        this.finalePointer = { x: event.clientX, y: event.clientY };
        if (this.finalePointerNear !== false) this.syncFinaleCursor();
      }, { passive: true, signal });
      window.addEventListener('scroll', () => this.syncFinaleCursor(), { passive: true, signal });
      window.addEventListener('resize', () => this.syncFinaleCursor(), { passive: true, signal });
      window.addEventListener('blur', () => {
        this.finalePointer = null;
        this.syncFinaleCursor();
      }, { signal });
      burstButton.addEventListener('click', () => this.burst(), { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.clearConfetti();
      }, { signal });
    }

    get motionEnabled() {
      return this.dataset.motion === 'true' && !this.reduced.matches && document.body.dataset.acMotion !== 'reduced';
    }

    setupMotion() {
      this.setupValuesCursor();
      this.stopCycle();
      this.clearConfetti();
      this.setFinaleReady(false);
      this.entranceReady = false;
      this.querySelectorAll('video').forEach(video => video.pause());
      this.media?.revert();
      this.media = null;
      this.classList.remove('is-scroll-active');
      const burstButton = this.querySelector('[data-about-burst]');
      if (burstButton) burstButton.hidden = !this.querySelector('.ac-about-finale__confetti');
      const gsap = window.gsap;
      const ScrollTrigger = window.ScrollTrigger;
      this.updateCycle();
      if (!gsap || !ScrollTrigger || !this.motionEnabled) {
        if (burstButton) this.setFinaleReady(true);
        return;
      }
      gsap.registerPlugin(ScrollTrigger);
      this.media = gsap.matchMedia();
      this.media.add('(prefers-reduced-motion: no-preference)', () => {
        if (this.dataset.scene === 'values') this.classList.add('is-scroll-active');
        this.querySelectorAll('[data-about-reveal]').forEach(element => {
          if (this.dataset.scene === 'possible') {
            gsap.from(element, { scaleY: 0, transformOrigin: 'bottom', duration: .7, ease: possibleEase, scrollTrigger: { trigger: element, start: element.matches('h2') ? 'top 75%' : 'top 100%', toggleActions: 'play none none reverse' } });
          } else {
            if (element.matches('.ac-about-values__badge')) return;
            gsap.from(element, { opacity: 0, y: 80, duration: .8, ease: 'power3.out', scrollTrigger: { trigger: element.closest('.ac-about-values__item') || element, start: 'top 80%', toggleActions: 'play none none reverse' } });
          }
        });
        const fills = this.querySelectorAll('.ac-about-possible__fill');
        if (fills.length) {
          const lines = gsap.timeline({ scrollTrigger: { trigger: this, start: 'top 75%', once: true } });
          fills.forEach((element, index) => lines.from(element, { clipPath: index ? 'inset(0 0 0 100%)' : 'inset(0 100% 0 0)', duration: 1.5, ease: possibleEase }, 0));
        }
        const chars = this.querySelectorAll('.ac-about-possible__char');
        if (chars.length) gsap.from(chars, {
          scaleY: 0, transformOrigin: 'bottom', duration: .7, stagger: .1, ease: possibleEase,
          scrollTrigger: { trigger: this.querySelector('.ac-about-possible__letters'), start: 'top 75%', once: true },
          onComplete: () => { this.entranceReady = true; this.updateCycle(); },
        });
        const finale = this.querySelector('.ac-about-finale__image');
        if (finale) {
          this.classList.add('is-scroll-active');
          const maskRange = () => window.innerWidth < 768 ? [450, 70] : window.innerWidth < 1024 ? [350, 50] : [270, 30];
          gsap.fromTo(finale, { maskSize: () => `${maskRange()[0]}% auto` }, {
            maskSize: () => `${maskRange()[1]}% auto`, ease: 'none',
            scrollTrigger: {
              trigger: this, start: 'top top', end: 'bottom bottom', scrub: true, invalidateOnRefresh: true,
              onUpdate: self => this.setFinaleReady(self.progress >= .8),
              onRefresh: self => this.setFinaleReady(self.progress >= .8),
            },
          });
          const footer = document.querySelector('[data-ac-ref-footer]');
          // One pin owner replaces CSS sticky and holds the canvas as the footer
          // covers it. Pin the plain stage, preserving custom-element lifecycle.
          ScrollTrigger.create({ trigger: this, start: 'top -10%', endTrigger: footer || this, end: footer ? 'top top' : 'bottom bottom', pin: this.querySelector('.ac-about-finale__stage'), pinSpacing: false });
          if (footer) {
            gsap.fromTo(finale, { yPercent: 0 }, { yPercent: 20, ease: 'none', scrollTrigger: { trigger: footer, start: 'top bottom', end: 'top top', scrub: true, invalidateOnRefresh: true } });
          }
        }
        return () => this.classList.remove('is-scroll-active');
      }, this);
      this.media.add('(max-width: 1023px) and (prefers-reduced-motion: no-preference)', () => {
        const badge = this.querySelector('.ac-about-values__badge');
        if (badge) gsap.from(badge, { opacity: 0, y: 80, duration: .8, ease: 'power3.out', scrollTrigger: { trigger: badge, start: 'top 80%', toggleActions: 'play none none reverse' } });
      }, this);
      this.media.add('(min-width: 768px) and (prefers-reduced-motion: no-preference)', () => {
        const mission = this.querySelector('.ac-about-mission__image');
        if (!mission) return;
        this.classList.add('is-scroll-active');
        const initial = () => {
          const gutter = Number.parseFloat(getComputedStyle(this).paddingLeft);
          const tablet = window.innerWidth < 1024;
          const unit = gutter / (tablet ? 3.2 : 3.5);
          const width = Math.min(this.clientWidth - gutter * 2, unit * (tablet ? 42.6 : 97.3));
          const ratio = getComputedStyle(mission).aspectRatio.split('/').map(Number);
          return { width, height: width / (ratio[0] / (ratio[1] || 1)), x: -gutter };
        };
        ScrollTrigger.create({
          trigger: this, start: 'top top', end: 'bottom top',
          pin: this.querySelector('.ac-about-mission__stage'), pinSpacing: false,
        });
        // Resizing this isolated frame preserves cover cropping; scaling the
        // image would magnify the subject instead of opening the composition.
        gsap.fromTo(mission, {
          width: () => initial().width, height: () => initial().height, x: () => initial().x,
        }, {
          width: () => window.innerWidth, height: () => window.innerHeight, x: 0,
          ease: 'none',
          scrollTrigger: {
            trigger: this,
            start: 'top 5%',
            end: 'bottom bottom',
            scrub: true,
            invalidateOnRefresh: true
          }
        });
        return () => this.classList.remove('is-scroll-active');
      }, this);
    }

    syncVideo(letter, playing) {
      this.querySelectorAll('video').forEach(video => {
        if (!letter.contains(video) || !playing) {
          video.pause();
          if (video.readyState) video.currentTime = 0;
        }
      });
      if (!playing || !this.motionEnabled) return;
      const video = letter.querySelector('video');
      if (!video) return;
      if (!video.src) video.src = video.dataset.src;
      video.play().catch(() => {});
    }

    stopCycle() {
      clearInterval(this.cycleTimer);
      this.cycleTimer = null;
    }

    updateCycle() {
      if (this.dataset.scene !== 'possible') return;
      const motionEnabled = this.isConnected && this.motionEnabled;
      const interaction = this.focusedLetter || this.hoveredLetter || this.querySelector('.ac-about-possible__letter[aria-pressed="true"]');
      if (!motionEnabled || !this.entranceReady || !this.lettersVisible || document.hidden || interaction) {
        this.stopCycle();
        this.querySelectorAll('.is-auto-active').forEach(letter => {
          letter.classList.remove('is-auto-active');
          if (letter !== interaction) letter.querySelector('video')?.pause();
        });
        if (!motionEnabled || !this.lettersVisible || document.hidden) this.querySelectorAll('video').forEach(video => video.pause());
        return;
      }
      if (this.cycleTimer) return;
      const letters = [...this.querySelectorAll('.ac-about-possible__letter')];
      if (!letters.length) return;
      const activate = () => {
        letters.forEach((letter, index) => letter.classList.toggle('is-auto-active', index === this.cycleIndex));
        this.syncVideo(letters[this.cycleIndex], true);
      };
      activate();
      this.cycleTimer = setInterval(() => { this.cycleIndex = (this.cycleIndex + 1) % letters.length; activate(); }, 1500);
    }

    setupValuesCursor() {
      if (this.dataset.scene !== 'values') return;
      this.stopValuesCursor();
      const cursor = this.querySelector('[data-about-values-cursor]');
      const gsap = window.gsap;
      if (!cursor || !gsap) return;
      const query = matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)');
      const controller = new AbortController();
      this.valuesCursorController = controller;
      this.valuesCursor = cursor;
      const { signal } = controller;
      let x = window.innerWidth / 2;
      let y = window.innerHeight / 2;
      let seen = false;
      let visible = false;
      let near = true;
      gsap.set(cursor, { x, y, xPercent: -50, yPercent: -50, opacity: 0, scale: .5, transformOrigin: 'center center' });
      const moveX = gsap.quickTo(cursor, 'x', { duration: .6, ease: 'power3.out' });
      const moveY = gsap.quickTo(cursor, 'y', { duration: .6, ease: 'power3.out' });
      this.valuesCursorMoveX = moveX;
      this.valuesCursorMoveY = moveY;
      const hide = () => {
        if (!visible) return;
        visible = false;
        if (this.motionEnabled) gsap.to(cursor, { opacity: 0, scale: .5, duration: .2, ease: 'power2.in', overwrite: 'auto' });
        else gsap.set(cursor, { opacity: 0, scale: .5 });
      };
      const sync = () => {
        this.classList.toggle('is-cursor-ready', query.matches);
        if (!query.matches || !seen || document.hidden || !near) { hide(); return; }
        const bounds = this.getBoundingClientRect();
        if (x < bounds.left || x > bounds.right || y < bounds.top || y > bounds.bottom) { hide(); return; }
        if (this.motionEnabled) { moveX(x); moveY(y); }
        else gsap.set(cursor, { x, y });
        if (visible) return;
        visible = true;
        if (this.motionEnabled) gsap.to(cursor, { opacity: 1, scale: 1, duration: .2, ease: 'power2.out', overwrite: 'auto' });
        else gsap.set(cursor, { opacity: 1, scale: 1 });
      };
      if ('IntersectionObserver' in window) {
        this.valuesCursorObserver = new IntersectionObserver(entries => {
          near = entries[0].isIntersecting;
          sync();
        }, { rootMargin: '100% 0px' });
        this.valuesCursorObserver.observe(this);
      }
      window.addEventListener('pointermove', event => {
        if (event.pointerType === 'touch') return;
        x = event.clientX;
        y = event.clientY;
        seen = true;
        if (near) sync();
      }, { passive: true, signal });
      window.addEventListener('scroll', sync, { passive: true, signal });
      window.addEventListener('blur', () => { seen = false; hide(); }, { signal });
      document.addEventListener('visibilitychange', sync, { signal });
      query.addEventListener('change', sync, { signal });
      this.addEventListener('pointerleave', hide, { signal });
      sync();
    }

    stopValuesCursor() {
      this.valuesCursorController?.abort();
      this.valuesCursorController = null;
      this.valuesCursorObserver?.disconnect();
      this.valuesCursorObserver = null;
      this.valuesCursorMoveX?.tween?.kill();
      this.valuesCursorMoveY?.tween?.kill();
      this.valuesCursorMoveX = null;
      this.valuesCursorMoveY = null;
      if (this.valuesCursor) window.gsap?.killTweensOf(this.valuesCursor);
      this.valuesCursor = null;
      this.classList.remove('is-cursor-ready');
    }

    syncFinaleCursor() {
      const cursor = this.querySelector('.ac-about-finale__cursor');
      const stage = this.querySelector('.ac-about-finale__stage');
      const pointer = this.finalePointer;
      if (!cursor || !stage || !pointer || !this.finePointer.matches || document.hidden || this.finalePointerNear === false) {
        this.classList.remove('is-pointer-active');
        return;
      }
      const bounds = stage.getBoundingClientRect();
      const inside = pointer.x >= bounds.left && pointer.x <= bounds.right && pointer.y >= bounds.top && pointer.y <= bounds.bottom;
      if (inside) cursor.style.transform = `translate3d(${pointer.x - bounds.left}px, ${pointer.y - bounds.top}px, 0)`;
      this.classList.toggle('is-pointer-active', Boolean(inside && this.finaleReady));
    }

    setFinaleReady(ready) {
      const button = this.querySelector('[data-about-burst]');
      if (!button) return;
      this.finaleReady = ready;
      button.disabled = !ready;
      this.classList.toggle('is-eligible', ready);
      this.syncFinaleCursor();
    }

    burst() {
      const layer = this.querySelector('.ac-about-finale__confetti');
      if (!this.finaleReady || !layer) return;
      const sources = [...new Set([...document.querySelectorAll('ac-about-scene[data-scene="possible"] .ac-about-possible__figure')]
        .map(image => image.currentSrc || image.src)
        .filter(Boolean))];
      if (!sources.length) return;
      const random = (min, max) => min + Math.random() * (max - min);
      const count = Math.min(Math.floor(random(5, 9)), sources.length);
      const remainingSources = [...sources];
      const [minWidth, maxWidth] = window.innerWidth < 768 ? [28, 48] : window.innerWidth < 1024 ? [40, 68] : [52, 88];
      const stage = this.querySelector('.ac-about-finale__stage').getBoundingClientRect();
      const bounds = layer.getBoundingClientRect();
      const originX = stage.left + stage.width / 2 - bounds.left;
      const originY = stage.top + stage.height / 2 - bounds.top;
      const gsap = window.gsap;
      for (let index = 0; index < count; index++) {
        const piece = document.createElement('img');
        piece.src = remainingSources.splice(Math.floor(Math.random() * remainingSources.length), 1)[0];
        piece.alt = '';
        piece.decoding = 'async';
        piece.style.width = `${random(minWidth, maxWidth)}px`;
        layer.append(piece);
        if (!this.motionEnabled || !gsap?.ticker) {
          piece.style.left = '50%';
          piece.style.top = '50%';
          piece.style.transform = 'translate(-50%, -50%)';
          this.confettiTimeouts ||= new Set();
          const timer = setTimeout(() => { piece.remove(); this.confettiTimeouts.delete(timer); }, 350);
          this.confettiTimeouts.add(timer);
          continue;
        }
        const highArc = Math.random() < .2;
        const sideways = (highArc ? random(.32, .7) : random(.42, .85)) * bounds.width * (Math.random() < .5 ? -1 : 1);
        const upwards = -random(window.innerHeight * (highArc ? .75 : .55), window.innerHeight * (highArc ? 1.05 : .85));
        const gravity = highArc ? random(1600, 2200) : random(1900, 2600);
        const friction = random(.82, .9);
        const delay = random(.06, .14);
        const rotation = random(-45, 45);
        const spin = random(220, 520) * (Math.random() < .5 ? -1 : 1);
        const x = originX + random(-12, 12);
        const y = originY + random(-10, 10);
        const scale = random(.9, 1.15);
        gsap.set(piece, { x, y, xPercent: -50, yPercent: -50, rotation, scale, opacity: 1 });
        this.confettiPieces ||= new Set();
        this.confettiPieces.add({ piece, start: performance.now(), delay, sideways, upwards, gravity, friction, rotation, spin, x, y, scale });
      }
      if (this.confettiPieces?.size && !this.confettiTick) {
        const tick = () => {
          const now = performance.now();
          for (const state of this.confettiPieces) {
            const { piece, start, delay, sideways, upwards, gravity, friction, rotation, spin, x, y, scale } = state;
            if (!piece.isConnected) { this.confettiPieces.delete(state); continue; }
            const elapsed = (now - start) / 1000;
            const travelTime = Math.max(0, elapsed - delay);
            const drag = sideways * friction ** (travelTime * 60);
            const horizontal = sideways * travelTime - (sideways - drag) * .02;
            const vertical = upwards * travelTime + .5 * gravity * travelTime * travelTime;
            piece.style.transform = `translate(-50%, -50%) translate(${x + horizontal}px, ${y + vertical}px) rotate(${rotation + spin * travelTime}deg) scale(${scale})`;
            if (elapsed > 2 && !piece.dataset.fading) {
              piece.dataset.fading = 'true';
              gsap.to(piece, { opacity: 0, duration: .25, overwrite: true });
            }
            if (elapsed > 2.3) {
              this.confettiPieces.delete(state);
              piece.remove();
            }
          }
          if (!this.confettiPieces.size) {
            gsap.ticker.remove(tick);
            this.confettiTickers.delete(tick);
            this.confettiTick = null;
          }
        };
        this.confettiTick = tick;
        this.confettiTickers ||= new Set();
        this.confettiTickers.add(tick);
        gsap.ticker.add(tick);
      }
    }

    clearConfetti() {
      this.confettiTickers?.forEach(tick => window.gsap?.ticker.remove(tick));
      this.confettiTickers?.clear();
      this.confettiTick = null;
      this.confettiPieces?.clear();
      this.confettiTimeouts?.forEach(timer => clearTimeout(timer));
      this.confettiTimeouts?.clear();
      this.querySelectorAll('.ac-about-finale__confetti img').forEach(piece => {
        window.gsap?.killTweensOf(piece);
        piece.remove();
      });
    }

    disconnectedCallback() {
      this.stopValuesCursor();
      this.stopCycle();
      this.entranceReady = false;
      this.hoveredLetter = null;
      this.focusedLetter = null;
      this.querySelectorAll('.is-auto-active').forEach(letter => letter.classList.remove('is-auto-active'));
      this.controller?.abort();
      this.controller = null;
      this.finalePointer = null;
      this.classList.remove('is-pointer-active');
      this.motionObserver?.disconnect();
      this.media?.revert();
      this.media = null;
      this.visibility?.disconnect();
      this.finalePointerObserver?.disconnect();
      this.finalePointerObserver = null;
      this.querySelectorAll('video').forEach(video => video.pause());
      this.clearConfetti();
    }
  }
  customElements.define('ac-about-scene', ACAboutScene);
})();
